import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  setProfileState,
  techProfileLinkChangeAction,
} from "../../../actions/MyTechProfile/myTechProfileActions";
import "./EditMyTechProfilePersonalDetails.css";

const EditMyTechProfilePersonalDetails = ({ scrollTo }) => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.editMyTechProfileStore);

  // Local state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [introTitle, setIntroTitle] = useState(""); // New state for Intro Title
  const [profilePicture, setProfilePicture] = useState("");
  const [githubLink, setGithubLink] = useState("");
  const [linkedinLink, setLinkedinLink] = useState("");
  const [resumeLink, setResumeLink] = useState("");

  // State for notification logic
  const [updatedField, setUpdatedField] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Populate local state from Redux
  useEffect(() => {
    if (profile) {
      setName(profile.name || "");
      setEmail(profile.email || "");
      setTitle(profile.title || "");
      setIntroTitle(profile.introTitle || ""); // Populate introTitle
      setProfilePicture(profile.profilePicture || "");
      setGithubLink(profile.githubLink || "");
      setLinkedinLink(profile.linkedinLink || "");
      setResumeLink(profile.resumeLink || "");
    }
  }, [profile]);

  // Handle "Click here" action
  const handleNoteClick = (field) => {
    console.log(`"Click here" pressed for: ${field}`);
    dispatch(techProfileLinkChangeAction(field));
    // You can add your logic here, for example, to scroll to the preview
  };

  // Handle input changes
  const handleChange = (field, value) => {
    dispatch(setProfileState({ ...profile, [field]: value }));

    switch (field) {
      case "name":
        setName(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "title":
        setTitle(value);
        break;
      case "introTitle": // Handle introTitle change
        setIntroTitle(value);
        break;
      case "profilePicture":
        setProfilePicture(value);
        break;
      case "githubLink":
        setGithubLink(value);
        break;
      case "linkedinLink":
        setLinkedinLink(value);
        break;
      case "resumeLink":
        setResumeLink(value);
        break;
      default:
        break;
    }

    if (["githubLink", "linkedinLink", "resumeLink"].includes(field)) {
      const fieldName = field.replace("Link", "");
      setUpdatedField(fieldName);
      setSyncing(true);
      setTimeout(() => {
        setSyncing(false);
      }, 2000);
    }
  };

  // Render the two-stage note
  const renderNote = (field) => {
    if (updatedField !== field) return null;

    return (
      <Box sx={{ mb: 2 }}>
        {syncing && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CircularProgress size={12} thickness={5} />
            <Typography variant="caption" color="textSecondary">
              We are syncing the updates...
            </Typography>
          </Box>
        )}
        {!syncing && (
          <Typography variant="caption" color="textSecondary">
            <strong>Note:</strong> Your {field} is updated and synced.{" "}
            <Button
              variant="text"
              size="small"
              onClick={() => handleNoteClick(field)}
              sx={{
                padding: 0,
                minWidth: "auto",
                textTransform: "none",
                verticalAlign: "baseline",
              }}
            >
              Click here
            </Button>{" "}
            to view the change.
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Name and Email Fields */}
      <div style={{ display: "flex", gap: "20px" }}>
        <div>
          <Typography variant="subtitle1" className="editmytechprofile-label">
            Edit Name
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={name}
            onChange={(e) => handleChange("name", e.target.value)}
            onFocus={() => scrollTo("home")}
            sx={{ marginBottom: "15px" }}
          />
        </div>
        <div>
          <Typography variant="subtitle1" className="editmytechprofile-label">
            Edit Email
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={email}
            onChange={(e) => handleChange("email", e.target.value)}
            onFocus={() => scrollTo("home")}
            sx={{ marginBottom: "15px" }}
          />
        </div>
      </div>

      {/* Intro Title Field */}
      <Typography variant="subtitle1" className="editmytechprofile-label">
        Edit Intro Title
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        value={introTitle}
        onChange={(e) => handleChange("introTitle", e.target.value)}
        onFocus={() => scrollTo("home")}
        sx={{ marginBottom: "15px" }}
      />

      {/* Title Field */}
      <Typography variant="subtitle1" className="editmytechprofile-label">
        Edit Your Title
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        value={title}
        onChange={(e) => handleChange("title", e.target.value)}
        onFocus={() => scrollTo("home")}
        sx={{ marginBottom: "15px" }}
      />

      {/* Profile Picture Field */}
      <Typography
        variant="subtitle1"
        className="editmytechprofile-label"
        sx={{ marginTop: "10px" }}
      >
        Upload Profile Picture (Google Drive link)
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        value={profilePicture}
        onChange={(e) => handleChange("profilePicture", e.target.value)}
        onFocus={() => scrollTo("home")}
        sx={{ marginBottom: "8px" }}
      />
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ marginBottom: "15px", display: "block" }}
      >
        Upload your profile picture to Google Drive, make it shareable, and
        paste the link here.
      </Typography>

      {/* Github Link Field */}
      <Typography variant="subtitle1" className="editmytechprofile-label">
        Github Link
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        value={githubLink}
        onChange={(e) => handleChange("githubLink", e.target.value)}
        onFocus={() => scrollTo("home")}
        sx={{ marginBottom: "8px" }}
      />
      {renderNote("github")}

      {/* Linkedin Link Field */}
      <Typography variant="subtitle1" className="editmytechprofile-label">
        Linkedin Link
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        value={linkedinLink}
        onChange={(e) => handleChange("linkedinLink", e.target.value)}
        onFocus={() => scrollTo("home")}
        sx={{ marginBottom: "8px" }}
      />
      {renderNote("linkedin")}

      {/* Resume Link Field */}
      <Typography variant="subtitle1" className="editmytechprofile-label">
        Resume Link
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        value={resumeLink}
        onChange={(e) => handleChange("resumeLink", e.target.value)}
        onFocus={() => scrollTo("home")}
        sx={{ marginBottom: "8px" }}
      />
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ marginBottom: "15px", display: "block" }}
      >
        Upload your resume to Google Drive, make it shareable, and paste the
        link here.
      </Typography>
      {renderNote("resume")}
    </Box>
  );
};

export default EditMyTechProfilePersonalDetails;
